import React, { useEffect, useState, useContext } from "react";
import { NewIndexViewModel, CallRowViewModel } from "../ViewModels/IndexContainerViewModels";
import { DecoratedRecordingTable } from "../RecordingTable/RecordingTable";
import FilterBar from "../Filtering/FilterBar";
import PageControlBar from "../Filtering/PageControlBar";
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper'
import { MsalContext } from '../Contexts/MsalContext';
import { useLocation } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import MonitoringContainer from "../Monitoring/MonitoringContainer";
import { APIContext } from "../Contexts/APIContext";
import TranscriptSearch, { eSearchModes } from "../Monitoring/Transcripts/TranscriptSearch";
import Alert from '@mui/material/Alert';
import Stack from "@mui/material/Stack";
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReportContainer from "../PowerBIReport/ReportContainer";
import CallTableViewModel, { CallTableColumns } from "../ViewModels/CallTableViewModel";
import ScorecardAnswersViewModel from "../Filtering/ReworkedFilters/ScorecardAnswerFilters/ViewModels/ScorecardAnswersViewModel";
import ClientReportViewModel from "../PowerBIReport/ViewModels/FlagstoneClientReportModel";
import OtherLinksContainer from "../PowerBIReport/OtherLinksContainer";
import Box from "@mui/material/Box";
import InsightContainer from "../Insight/InsightContainer";
import { InsightView } from "../ViewModels/InsightView";
import { formatDate } from "../Utilities/UtilityMethods";

const styles = {
    selectedTab: {
        borderRadius: 0,
        padddingLeft: '16px',
        paddigRight: '16px',
        borderTop: 'solid 1px black',
        borderLeft: 'solid 1px black',
        borderRight: 'solid 1px black',

    },
    tab: {
        backgroundColor: '#1D8ABF',
        color: '#fff',
        borderRadius: 0,
        borderBottom: 'solid 1px black',
        padddingLeft: '16px',
        paddigRight: '16px',
        '&:hover': {
            backgroundColor: '#1D8ABF'
        }
    }
}

interface IIndexContainerProps {
    readonly: boolean;
    defaultCustomerIDFilter: string | null;
    showModal?: (systemPK: string) => void;
    padding?: boolean;
    toggleNewFilter?: () => void;
    monitoring?: boolean;
}

interface Tab {
    id: number;
    name: string;
    isAI: boolean;
    requiresExec: boolean;
    filterBarEnabled: boolean;
    applyFilterAlertEnabled: boolean;
    disabled: boolean;
}

export default function IndexContainer({ padding, defaultCustomerIDFilter, readonly, showModal, toggleNewFilter, monitoring }: IIndexContainerProps) {
    const { get, isClient, isExec, isQA, isAdmin, isResqAnywhere, isClientAIEnabled, isRTM, isTeamLeader, isQATeamLeader, isAgent, isDataDialler } = useContext(APIContext);
    const { search, state } = useLocation();
    const params = new URLSearchParams(search);
    const client = params.get("client") ?? '';
    const templateId = params.get("templateId") ?? '';
    const sectionId = params.get("sectionId") ?? '';
    const questionId = params.get('questionId') ?? '';
    const dateRangeFlag = params.get('dateRangeFlag') ?? '';
    const guid = params.get("filter") ?? '';
    const answer = params.get("answer") ?? '';
    const { msalFetchResponseOk } = useContext(MsalContext);
    const [data, setData] = useState(new NewIndexViewModel());
    const [loaded, setLoaded] = useState(true);
    const [searchFailedMessage, setSearchFailedMessage] = useState("");
    const [showAlert, setShowAlert] = useState(true);
    const [modalPK, setModalPK] = useState<string | null>(null);
    const [transcriptSearch, setTranscriptSearch] = useState<TranscriptSearch | undefined>(undefined);
    const [callTableViewModel, setCallTableViewModel] = useState<CallTableViewModel>(new CallTableViewModel(client));
    const [clientReport, setClientReport] = useState<ClientReportViewModel>(new ClientReportViewModel());
    const [enableFilterFunctions, setEnableFilterFunctions] = useState(readonly);
    const [isClientScorecardEnabled, setIsClientScorecardEnabled] = useState(false);
    const [isOperationsScorecardEnabled, setIsOperationsScorecardEnabled] = useState(false);

    useEffect(() => {
        if (isClient) {
            get('User', 'IsClientScorecardEnabled', (result: boolean) => {
                setIsClientScorecardEnabled(result);
            });
        }
        if (isTeamLeader) {
            get('User', `IsOperationsScorecardEnabled?client=${client}`, (result: boolean) => {
                setIsOperationsScorecardEnabled(result);
            });
        }
    }, [isClient, isTeamLeader]);

    const userMode: string =
        isAdmin ? "Admin" :
            isExec ? "Exec" :
                isClientAIEnabled ? "ClientAI" :
                    isClient ? "Client" :
                        isTeamLeader ? "TeamLeader" :
                            isDataDialler ? "Dialler" :
                                isAgent ? "Agent" :
                                    (isQA || isQATeamLeader) ? "QA" :
                                        isRTM ? "RTM" : "";

    //#region tab logic
    const [index, setIndex] = useState(0);
    const [tabs, setTabs] = useState<Tab[]>([
        { id: 0, name: "Search", isAI: false, requiresExec: false, filterBarEnabled: true, applyFilterAlertEnabled: true, disabled: false },
        { id: 1, name: "Reports", isAI: true, requiresExec: false, filterBarEnabled: true, applyFilterAlertEnabled: false, disabled: false },
        { id: 2, name: "Agent Insights", isAI: false, requiresExec: false, filterBarEnabled: false, applyFilterAlertEnabled: false, disabled: true },
        { id: 3, name: "Customer Insights", isAI: false, requiresExec: false, filterBarEnabled: false, applyFilterAlertEnabled: false, disabled: true },
        { id: 4, name: "Operational Insights", isAI: false, requiresExec: false, filterBarEnabled: false, applyFilterAlertEnabled: false, disabled: true },
        { id: 5, name: "Other Links", isAI: false, requiresExec: false, filterBarEnabled: false, applyFilterAlertEnabled: false, disabled: false },
    ]);

    useEffect(() => {
        if (isAdmin || isExec) { //admins and execs should see all insights tabs with no need for an api call to check if they are enabled
            let updatedTabs = [...tabs];
            updatedTabs.find(tab => tab.name === 'Agent Insights')!.disabled = false;
            updatedTabs.find(tab => tab.name === 'Operational Insights')!.disabled = false;
            updatedTabs.find(tab => tab.name === 'Customer Insights')!.disabled = false;
            setTabs(updatedTabs);
        }
        else if (isClient) //permission specific checks
            msalFetchResponseOk(`api/User/IsClientInsightsEnabled`)
                .then(response => response.json())
                .then(data => {
                    let updatedTabs = [...tabs];

                    updatedTabs.find(tab => tab.name === 'Agent Insights')!.disabled = !data.agentInsightsEnabled;
                    updatedTabs.find(tab => tab.name === 'Operational Insights')!.disabled = !data.operationalInsightsEnabled;
                    updatedTabs.find(tab => tab.name === 'Customer Insights')!.disabled = !data.customerInsightsEnabled;
                    setTabs(updatedTabs);
                })
                .catch(e => console.error(e));
    }, []);

    useEffect(() => {
        if (isTeamLeader)
            msalFetchResponseOk(`api/User/IsOperationsInsightsEnabled?client=${client}`)
                .then(response => response.json())
                .then(data => {
                    let updatedTabs = [...tabs];

                    updatedTabs.find(tab => tab.name === 'Agent Insights')!.disabled = !data.agentInsightsEnabled;
                    updatedTabs.find(tab => tab.name === 'Operational Insights')!.disabled = !data.operationalInsightsEnabled;
                    updatedTabs.find(tab => tab.name === 'Customer Insights')!.disabled = !data.customerInsightsEnabled;
                    setTabs(updatedTabs);
                })
                .catch(e => console.error(e));
    }, [client]);
    //#endregion

    useEffect(() => {
        getClientReport(client);

        if (guid !== null && guid !== undefined && guid !== "") {
            try { setEnableFilterFunctions(state.readonly); }
            catch { setEnableFilterFunctions(readonly); }

            applySaved(guid);
            return;
        }

        setEnableFilterFunctions(readonly);
        const initialCallTableViewModel = new CallTableViewModel(client);

        const validDateTimePresets = ['This Week', 'Last Week', 'This Month', 'Last Month']

        if (validDateTimePresets.includes(dateRangeFlag)) {
            initialCallTableViewModel.filters.dateTimeFilters.preset = dateRangeFlag;
        }

        if (!isNaN(parseInt(templateId)) && !isNaN(parseInt(sectionId)) && !isNaN(parseInt(questionId))) {
            if (answer) {
                let scorecardAnswer = new ScorecardAnswersViewModel();
                scorecardAnswer.isActive = true;
                scorecardAnswer.templateId = Number(templateId);
                scorecardAnswer.sectionId = Number(sectionId);
                scorecardAnswer.questionId = Number(questionId);
                scorecardAnswer.answer = answer;
                initialCallTableViewModel.filters.scorecardAnswer = scorecardAnswer;
            }
            else {
                initialCallTableViewModel.filters.aiFlaggedIssues.isActive = true;
                initialCallTableViewModel.filters.aiFlaggedIssues.templateId = Number(templateId);
                initialCallTableViewModel.filters.aiFlaggedIssues.sectionId = Number(sectionId);
                initialCallTableViewModel.filters.aiFlaggedIssues.questionId = Number(questionId);
            }
        }

        if (defaultCustomerIDFilter !== null) {
            let oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

            initialCallTableViewModel.filters.dateTimeFilters.preset = 'Date Range';
            initialCallTableViewModel.filters.dateTimeFilters.startDate = oneYearAgo;

            initialCallTableViewModel.filters.customerFilters.isActive = true;
            initialCallTableViewModel.filters.customerFilters.customerID = defaultCustomerIDFilter;
        }

        //based on current perms decide what columns are aviable for the user
        initialCallTableViewModel.columns.setShownStateByMode(userMode, isClientScorecardEnabled, isOperationsScorecardEnabled);
        setCallTableViewModel(initialCallTableViewModel);

    }, [guid, client, isClientScorecardEnabled, isOperationsScorecardEnabled]);

    useEffect(() => {
        if (data.callTableViewModel.filters.transcriptFilters.transcriptMode != '') {
            var transcriptSearchVM = new TranscriptSearch();
            transcriptSearchVM.searchText = data.callTableViewModel.filters.transcriptFilters.searchValues;

            // Set which speaker channel to highlight
            data.callTableViewModel.filters.transcriptFilters.transcriptOptions === "Agent Transcript Search"
                ?
                transcriptSearchVM.setSpeakerToAgent()
                :
                transcriptSearchVM.setSpeakerToCustomer()

            // Sets the search mode, which determines the logic for which words are highlighted
            if (data.callTableViewModel.filters.transcriptFilters.searchMode === 'All Word(s)') {
                transcriptSearchVM.setSearchMode(eSearchModes.All);
            }
            if (data.callTableViewModel.filters.transcriptFilters.searchMode === 'Any Word(s)') {
                transcriptSearchVM.setSearchMode(eSearchModes.Any);
            }
            if (data.callTableViewModel.filters.transcriptFilters.searchMode === 'Exact Phrase') {
                transcriptSearchVM.setSearchMode(eSearchModes.Phrase);
            }
            if (data.callTableViewModel.filters.transcriptFilters.searchMode === 'Similar Word(s)') {
                transcriptSearchVM.setSearchMode(eSearchModes.Similar);
                transcriptSearchVM.setSimilarWords(data.similarWords);
            }

            setTranscriptSearch(transcriptSearchVM)
            return;
        }

        setTranscriptSearch(undefined);
    }, [data])

    useEffect(() => {
        setData(new NewIndexViewModel());
    }, [callTableViewModel, showAlert])

    const changePage = (page: number) => {
        const initialCallTableViewModel = { ...callTableViewModel };
        initialCallTableViewModel.filters.pageNumber = page;
        updateStateFilters(initialCallTableViewModel, true);
    }

    const updateStateFilters = (initalCallTableViewModel: CallTableViewModel, apply: boolean = false) => {
        if (!apply) {
            setShowAlert(true);
            return;
        }

        filterSearch(initalCallTableViewModel);
        setShowAlert(false);
    }

    const filterSearch = (initialCallTableViewModel?: CallTableViewModel) => {
        setLoaded(false);
        msalFetchResponseOk('api/Search/ApplyFilter/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
            },
            body: JSON.stringify(initialCallTableViewModel === undefined ? callTableViewModel : initialCallTableViewModel)
        })
            .then(response => response.json())
            .then(data => {
                setLoaded(true);
                if (data.success) {
                    setData(data.value);
                    return;
                }

                setData(new NewIndexViewModel());
                setSearchFailedMessage(data.messages[0]);
            })
            .catch(e => {
                console.error(e);
                setLoaded(true);
                setData(new NewIndexViewModel());
                setSearchFailedMessage('The search could not be completed. Please try again.');
            });
    }

    const applySaved = (guid: string) => {
        setLoaded(false);
        setShowAlert(true);

        msalFetchResponseOk(`api/Search/ApplySavedFilter?guid=${guid}`)
            .then(response => response.json())
            .then((data: NewIndexViewModel) => {
                data.callTableViewModel.filters.client = client; //set client to match the url parameter << needed as shared parameters have no clinet 
                setData(data);
                setLoaded(true);

                //based on current perms decide what columns are aviable for the user
                let initialCallTableViewModelColumns: CallTableColumns = Object.assign(new CallTableColumns(), data.callTableViewModel.columns);
                initialCallTableViewModelColumns.setShownStateByMode(userMode, isClientScorecardEnabled, isOperationsScorecardEnabled);

                let initialCallTableViewModel: CallTableViewModel = Object.assign(new CallTableViewModel(), data.callTableViewModel);
                initialCallTableViewModel.columns = initialCallTableViewModelColumns;

                setCallTableViewModel(initialCallTableViewModel);
            }).catch(e => {
                console.error(e);
                //reset so the previously loaded filters can't be applied while the url points at the saved filter that failed
                setData(new NewIndexViewModel());
                setCallTableViewModel(new CallTableViewModel(client));
                setLoaded(true);
                setSearchFailedMessage('The saved filter could not be applied. Please try again.');
            });
    }

    const bulkDownload = () => {
        setLoaded(false);
        msalFetchResponseOk('api/Search/BulkDownload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
            },
            body: JSON.stringify(callTableViewModel)
        })
            .then(response => response.blob())
            .then(data => {
                setLoaded(true);
                if (data.type !== "application/problem+json") {
                    const dateTime = new Date();
                    const formattedDate = formatDate(dateTime);
                    const formattedTime = dateTime.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
                    const url = window.URL.createObjectURL(data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = "CallRecordings_" + formattedDate + "_" + formattedTime + ".zip";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    return;
                }

                console.log('error', 'An internal server error occured');
            })
            .catch(e => {
                console.error(e);
                setLoaded(true);
            });
    }

    const closeFailedModal = () => {
        setSearchFailedMessage('');
    }

    const openMonitoring = (systemPK: string) => {
        setModalPK(systemPK);
    };

    const closeMonitoring = () => {
        const currRow: CallRowViewModel = data.callRows.filter(row => row.systemPK === modalPK)[0];
        get('MonitoringScorecard', `UpdateCallRow?callId=${modalPK}`, (callRowData) => {
            if (currRow) {
                const rows = [...data.callRows];
                currRow.tagsFlag = callRowData.hasTags;
                currRow.scorecardStatus = callRowData.scorecardStatus;
                currRow.scorecardCritFail = callRowData.critFail;
                rows.splice(rows.indexOf(currRow), 1, currRow);
                const actionsIndexViewModel: NewIndexViewModel = { ...data };
                actionsIndexViewModel.callRows = rows;
                setData(actionsIndexViewModel);
            }
        });
        setModalPK(null);
    }

    const getClientReport = (client: string) => {
        msalFetchResponseOk(`api/ClientReport/GetClientReportId?client=${client}`)
            .then(response => response.json())
            .then(data => {
                setClientReport(data);
            })
            .catch(e => console.error(e));
    }

    return (
        <Box style={{ padding: padding !== null && padding === true ? '8px' : '0px', height: '100%' }} display="flex" flexDirection="column" >
            {!monitoring && !enableFilterFunctions &&
                <Box>
                    <Stack direction="row">
                        {tabs.map((tab, i) => (
                            <React.Fragment key={`tab-${i}`}>
                                {
                                    (!tab.requiresExec || (tab.requiresExec && isExec)) &&
                                    <Button
                                        key={tab.id}
                                        fullWidth
                                        disabled={tab.disabled} //remove this when the relevent insight tabs are ready
                                        onClick={() => setIndex(tab.id)}
                                        sx={index === tab.id ? styles.selectedTab : styles.tab}
                                        style={index !== tab.id && i !== tabs.length - 1 && i !== index - 1 ? { borderRight: '1px white solid' } : {}}
                                    >
                                        {tab.name === 'Records' && <AssessmentIcon />}
                                        {(tab.name === 'Reports' && isClient) ? "Client Reports" : tab.name}
                                    </Button>
                                }
                            </React.Fragment>
                        ))}
                    </Stack>
                </Box>
            }
            <Box sx={{
                overflow: 'hidden',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                borderRight: 'solid 1px black',
                borderLeft: 'solid 1px black',
                borderBottom: 'solid 1px black',
                borderTop: enableFilterFunctions ? 'solid 1px black' : '',
            }}>
                {(!monitoring && tabs[index].filterBarEnabled) &&
                    <Box >
                        <FilterBar
                            client={client}
                            updateFilters={updateStateFilters}
                            loaded={false} isRTM={isRTM}
                            isClient={isClient}
                            readonly={enableFilterFunctions}
                            rowsCount={data.callTableViewModel.filters.count}
                            bulkDownload={bulkDownload}
                            toggleNewFilter={toggleNewFilter}
                            similarWords={data.similarWords ?? []}
                            isAgent={false}
                            isReport={tabs[index].name === 'Records'}
                            showDownloadButton={tabs[index].name === 'Search'}
                            callTableViewModel={callTableViewModel}
                            setCallTableViewModel={setCallTableViewModel} />
                        {(showAlert && tabs[index].applyFilterAlertEnabled) &&
                            <Alert severity="info">After selecting your filters, be sure to click "Apply Filters" to view your results.</Alert>
                        }
                    </Box>
                }
                <Box sx={tabs[index].name === 'Search' ? { display: 'block', flex: 1, height: '1px' } : { width: '0px', height: '0px', visibility: 'hidden' }}>
                    {loaded ?
                        <DecoratedRecordingTable
                            rows={data.callRows}
                            openMonitoring={openMonitoring}
                            isRTM={isRTM}
                            isClient={isClient}
                            isQA={isQA}
                            monitoring={monitoring}
                            isAdmin={isAdmin}
                            isResqAnywhere={isResqAnywhere}
                            isClientAIEnabled={isClientAIEnabled}
                            columns={callTableViewModel.columns}
                            hideTable={tabs[index].name !== 'Search'}
                        />
                        :
                        <>
                            <Paper style={{ height: '100%' }}>
                                <CircularProgress size={250} style={{ marginLeft: '40%', marginRight: '40%', marginTop: '200px' }} />
                            </Paper>
                        </>
                    }
                </Box>
                <Box sx={{ display: tabs[index].name === 'Search' ? 'block' : 'none' }}>
                    {!monitoring &&
                        <PageControlBar
                            pageNumber={data.callTableViewModel.filters.pageNumber}
                            pageCount={data.callTableViewModel.filters.pageCount}
                            rowCount={data.callTableViewModel.filters.count}
                            onChangePage={changePage} />
                    }
                </Box>
                {
                    tabs[index].name === 'Agent Insights' &&
                    <InsightContainer client={client} view={InsightView.Agent} />
                }
                {
                    tabs[index].name === 'Customer Insights' &&
                    <InsightContainer client={client} view={InsightView.Customer} />
                }
                {
                    tabs[index].name === 'Operational Insights' &&
                    <InsightContainer client={client} view={InsightView.Operational} />
                }
                <ReportContainer
                    filters={callTableViewModel}
                    todayReportId={isClient ? clientReport.clientTodayGuid : clientReport.todayGuid}
                    historicReportId={isClient ? clientReport.clientHistoricGuid : clientReport.historicGuid}
                    isSelected={tabs[index].name === 'Reports'}
                />
                <OtherLinksContainer
                    otherLinksId={isClient ? clientReport.externalOtherLinksGuid : clientReport.internalOtherLinksGuid}
                    isSelected={tabs[index].name === 'Other Links'}
                />
            </Box>





            <Dialog
                open={modalPK !== null}
                onClose={closeMonitoring}
                fullScreen
                PaperProps={{
                    style: {
                        padding: 0
                    }
                }}
            >
                {modalPK !== null &&
                    <MonitoringContainer
                        id={modalPK}
                        setId={setModalPK}
                        close={closeMonitoring}
                        transcriptSearch={transcriptSearch}
                        mainView={true}
                        showBackButton
                    />
                }
            </Dialog>
            <Dialog
                open={searchFailedMessage.length > 0}
                onClose={closeFailedModal}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Search Failed"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {searchFailedMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFailedModal} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
}